import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import http, { formatApiErrorDetail } from "@/api";
import { PILLARS, RECOGNITION_META, toBase10 } from "@/constants";
import { useAuth } from "@/auth";
import { RotateCw, Save, Sparkles, Plus, Trash2, ToggleLeft, ToggleRight, RefreshCw, Webhook, FileCheck, FileText, Download, ArrowRight, CheckCircle2, CircleDashed, X, Upload } from "lucide-react";
import Modal from "@/components/Modal";

const FLOW_STEPS = [
  { key: "weights", tab: "weights", label: "Pesos por pilar", desc: "Cuánto pesa cada pilar en el score Elite",
    guide: { falta: "Los pesos de los 6 pilares no suman 100%.", pasos: ["Ajusta el peso de cada pilar (o usa 'Distribuir equitativo')", "Verifica que el total marque 100%", "Guarda y, si ya hay evaluaciones, recalcula los scores"] } },
  { key: "subkpis", tab: "subkpis", label: "Sub-KPIs", desc: "Desglose ponderado dentro de cada pilar",
    guide: { falta: "No hay ningún sub-KPI activo definido.", pasos: ["Elige el pilar a desglosar", "Agrega sub-indicadores con su ponderación interna", "Activa el switch 'usar sub-KPIs' si quieres que alimenten el score"] } },
  { key: "kpi_catalog", to: "/admin/catalogo-kpis", label: "Catálogo de KPIs", desc: "Indicadores, niveles 1–4 y frecuencia",
    guide: { falta: "El catálogo de KPIs está vacío.", pasos: ["Da de alta indicadores con código, pilar y categoría de compra", "Define sus niveles 1–4 (qué significa cada nivel)", "Asigna la periodicidad: mensual, bimestral, trimestral, semestral o anual"] } },
  { key: "phase_periodicity", to: "/admin/programa", label: "Fase y periodicidad", desc: "Fase 1/2 y frecuencia por pilar",
    guide: { falta: "Falta definir la fase del programa o la periodicidad por pilar.", pasos: ["Elige Fase 1 (proveedor en modo visor) o Fase 2 (activo)", "Define la frecuencia de evaluación de cada pilar", "Guarda la configuración"] } },
  { key: "recognition", tab: "recognition", label: "Reconocimiento", desc: "Rangos Platino–Bronce y reglas (escala 1–4)",
    guide: { falta: "Las reglas de reconocimiento usan los valores por defecto del glosario.", pasos: ["Ajusta promedios mínimo/máximo por nivel", "Define el mínimo por pilar y # de pilares requeridos", "Guarda — el recálculo es inmediato, sin despliegues"] } },
  { key: "criteria", tab: "criteria", label: "Criterios documentales", desc: "Evidencia requerida por pilar",
    guide: { falta: "No hay criterios documentales activos.", pasos: ["Define qué evidencia se requiere por pilar (certificados, políticas…)", "Marca cada criterio como activo", "Indica si aplica a nivel programa o por pilar"] } },
  { key: "questions", to: "/admin/preguntas", label: "Preguntas del cuestionario", desc: "Cuestionario que alimenta el scorecard",
    guide: { falta: "No hay preguntas activas en el cuestionario.", pasos: ["Crea preguntas por pilar", "Marca cuáles alimentan el scorecard", "Actívalas para que aparezcan en el flujo de evaluación"] } },
  { key: "users", to: "/admin/usuarios", label: "Usuarios y permisos", desc: "Evaluadores, pilares y alcances",
    guide: { falta: "Ningún evaluador tiene pilares asignados.", pasos: ["Presiona 'Editar' en un evaluador", "Pestaña Alcance → 'Pilares asignados' → elige su(s) pilar(es)", "Con eso solo podrá capturar SU pilar; el resto lo verá en modo lectura"] } },
  { key: "learning", to: "/admin/aprendizaje", label: "Centro de aprendizaje", desc: "Recursos de desarrollo al proveedor",
    guide: { falta: "No hay recursos publicados en el Centro de aprendizaje.", pasos: ["Agrega videos, infografías o documentos", "Etiquétalos por pilar y nivel del proveedor", "Publícalos para que aparezcan en /aprendizaje"] } },
  { key: "feedback360", to: "/admin/feedback-lala", label: "Feedback 360°", desc: "Cómo los proveedores califican a Lala",
    guide: { falta: "No hay preguntas activas del cuestionario inverso (proveedor → Lala).", pasos: ["Revisa las 6 dimensiones (comunicación, pagos, trato justo…)", "Activa o agrega preguntas", "Los proveedores responderán desde 'Voz del proveedor'"] } },
];

function RecognitionRulesTab() {
  const [rules, setRules] = useState(null);
  const [recoveryLabel, setRecoveryLabel] = useState("Recuperación");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => {
    http.get("/recognition/rules").then((r) => {
      setRules(r.data.rules);
      setRecoveryLabel(r.data.meta?.requiere_desarrollo?.label || "Recuperación");
    });
  }, []);
  if (!rules) return <div className="px-6 py-12 text-center font-hand text-xl text-[var(--lala-blue)]">Cargando…</div>;

  const LEVELS = [["bronce", "LALA Elite Bronce", "#B87333"], ["plata", "LALA Elite Plata", "#94A3B8"],
                  ["oro", "LALA Elite Oro", "#D4A437"], ["platino", "LALA Elite Platino", "#0033AD"]];
  const FIELDS = [["min_avg", "Promedio mínimo"], ["max_avg", "Promedio máximo"], ["min_pillar", "Mínimo por pilar"],
                  ["min_pillars_at", "# pilares requeridos"], ["at_threshold", "…con calificación ≥"]];
  const set = (lvl, k, v) => setRules({ ...rules, [lvl]: { ...rules[lvl], [k]: v } });
  const save = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      const payload = {};
      for (const [lvl] of LEVELS) {
        payload[lvl] = Object.fromEntries(Object.entries(rules[lvl]).map(([k, v]) => [k, Number(v)]));
      }
      const { data } = await http.put("/admin/recognition/rules", { rules: payload });
      setRules(data.rules);
      setMsg("Reglas guardadas. El reconocimiento se recalcula al instante en toda la plataforma.");
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="sketch-border bg-[var(--cream-soft)] p-6" data-testid="recognition-rules-form">
      <div className="font-hand text-xl text-[var(--lala-blue)]">motor de reconocimiento · escala 1–4</div>
      <h3 className="font-display text-2xl mb-1">Rangos y reglas por nivel</h3>
      <p className="text-sm text-[var(--ink-soft)] mb-4">
        Valores por defecto del glosario oficial (jul 2026). Editables sin despliegues — el recálculo aplica de inmediato.
        Un proveedor obtiene el nivel más alto cuyo promedio, piso por pilar y # de pilares destacados cumpla.
        <span className="block mt-1 text-xs" data-testid="base10-note">
          Nota: los umbrales se definen en la escala interna 1–4; en dashboards y perfiles los resultados se
          presentan en <strong>base 10</strong> (×2.5 — ej. 3.80 se muestra como 9.5).
        </span>
      </p>
      <div className="mb-4 p-3 rounded-lg bg-[#FDEBEE] border border-[#E0162B]/40 text-sm" data-testid="requiere-desarrollo-note">
        <strong className="text-[#8F0E1D]">⚠ {recoveryLabel}:</strong> todo proveedor con promedio menor a{" "}
        <strong>{rules.bronce?.min_avg}</strong> (o que no cumpla el piso por pilar de ningún nivel) queda sin metal,
        bajo observación, y se marca con alerta de plan de desarrollo en dashboards, perfil y reportes.
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <label className="text-xs font-bold text-[#8F0E1D]">Nombre de este nivel (configurable):</label>
          <input type="text" value={recoveryLabel} maxLength={40}
            onChange={(e) => setRecoveryLabel(e.target.value)}
            className="input-field text-sm w-56" data-testid="recovery-label-input" />
          <button disabled={busy || !recoveryLabel.trim()}
            onClick={async () => {
              setBusy(true); setMsg(""); setErr("");
              try {
                await http.put("/admin/recognition-label", { label: recoveryLabel.trim() });
                setMsg(`El nivel inferior ahora se llama "${recoveryLabel.trim()}" en toda la plataforma.`);
              } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
              finally { setBusy(false); }
            }}
            className="sketch-pill px-3 py-1.5 text-xs font-bold border border-[#8F0E1D] text-[#8F0E1D] bg-white disabled:opacity-40"
            data-testid="recovery-label-save-btn">
            Renombrar nivel
          </button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {LEVELS.map(([lvl, label, color]) => (
          <div key={lvl} className="p-4 rounded-xl bg-[var(--cream)] border-2" style={{ borderColor: color }} data-testid={`recognition-rule-${lvl}`}>
            <div className="font-bold text-sm mb-2" style={{ color }}>◆ {label}</div>
            <div className="grid grid-cols-2 gap-2">
              {FIELDS.map(([k, flabel]) => (
                <label key={k} className="text-xs">
                  <span className="text-[var(--ink-soft)]">{flabel}</span>
                  <input type="number" step={k === "min_pillars_at" ? 1 : 0.05} min={0} value={rules[lvl][k]}
                    onChange={(e) => set(lvl, k, e.target.value)}
                    className="input-field w-full text-sm font-semibold mt-0.5" data-testid={`rule-${lvl}-${k}`} />
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button onClick={save} disabled={busy}
          className="sketch-pill bg-[var(--lala-blue)] text-[var(--cream)] px-5 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="save-recognition-rules-btn">
          <Save size={14} /> Guardar reglas
        </button>
        {msg && <span className="text-sm text-emerald-700 font-semibold" data-testid="recognition-rules-msg">{msg}</span>}
        {err && <span className="text-sm text-red-700 font-semibold">{err}</span>}
      </div>
    </div>
  );
}

function ConfigFlow({ tab, setTab, nav }) {
  const [status, setStatus] = useState(null);
  const [tourMsg, setTourMsg] = useState("");
  const [guideStep, setGuideStep] = useState(null);
  useEffect(() => {
    http.get("/admin/config-status").then((r) => setStatus(r.data.status)).catch(() => setStatus({}));
  }, []);

  const goTo = (s) => (s.tab ? setTab(s.tab) : nav(s.to));
  const onStepClick = (s) => {
    if (status && status[s.key] === false) setGuideStep(s);
    else goTo(s);
  };

  const resetTours = async () => {
    if (!window.confirm("¿Reactivar los tours guiados para TODOS los usuarios? En su próxima visita verán el tour de nuevo (admins y evaluadores).")) return;
    try {
      const { data } = await http.post("/admin/tour/reset");
      setTourMsg(`Tours reactivados (v${data.version}). Todos verán el tour en su próxima entrada.`);
    } catch (e) {
      setTourMsg("No se pudo reactivar: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="mb-6" data-testid="config-flow">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="font-hand text-lg text-[var(--lala-blue)]">flujo de configuración del programa · {FLOW_STEPS.length} pasos</div>
        <div className="flex items-center gap-2">
          {tourMsg && <span className="text-xs text-emerald-700 font-semibold" data-testid="reset-tours-msg">{tourMsg}</span>}
          <button onClick={resetTours} data-testid="reset-tours-btn"
            className="sketch-pill bg-[var(--cream)] border border-[var(--lala-blue)] text-[var(--lala-blue)] px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 hover:bg-[var(--lala-blue)] hover:text-[var(--cream)] transition-colors"
            title="Todos los usuarios volverán a ver su tour guiado en la próxima visita">
            <Sparkles size={12} /> Reactivar tours para todos
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-y-3">
        {FLOW_STEPS.map((s, i) => {
          const active = s.tab && tab === s.tab;
          const ok = status?.[s.key];
          return (
            <div key={s.key} className="flex items-center">
              <button onClick={() => onStepClick(s)} data-testid={`flow-step-${s.key}`} title={s.desc}
                className={`sketch-pill text-left px-3 py-2 w-[152px] border transition-colors ${active ? "bg-[var(--lala-blue)] text-[var(--cream)] border-[var(--lala-blue)]" : "bg-[var(--cream)] text-[var(--lala-blue)] border-[var(--lala-blue)]/40 hover:border-[var(--lala-blue)]"}`}>
                <div className="flex items-center gap-1.5">
                  <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 ${active ? "bg-[var(--cream)] text-[var(--lala-blue)]" : "bg-[var(--lala-blue)] text-[var(--cream)]"}`}>{i + 1}</span>
                  <span className="text-xs font-bold leading-tight flex-1">{s.label}</span>
                  {status && (ok
                    ? <CheckCircle2 size={13} className={active ? "text-[var(--cream)]" : "text-emerald-600"} data-testid={`flow-status-${s.key}-ok`} />
                    : <CircleDashed size={13} className="text-amber-500" data-testid={`flow-status-${s.key}-pending`} />)}
                </div>
                <div className={`text-[10px] mt-1 leading-snug ${active ? "text-[var(--cream)]/85" : "text-[var(--ink-soft)]"}`}>{s.desc}</div>
              </button>
              {i < FLOW_STEPS.length - 1 && <ArrowRight size={14} className="mx-1 text-[var(--lala-blue)]/50 shrink-0" />}
            </div>
          );
        })}
      </div>
      <div className="text-[11px] text-[var(--ink-soft)] mt-2">
        <CheckCircle2 size={11} className="inline text-emerald-600 mr-0.5" /> configurado · <CircleDashed size={11} className="inline text-amber-500 mr-0.5" /> pendiente — el orden va de la definición del score (1–5) a la operación y gobernanza (6–10).
      </div>

      {guideStep && (
        <Modal testId="flow-guide-modal" align="center" zIndex="z-[90]" backdrop="bg-[rgba(10,20,40,0.55)]"
          onClose={() => setGuideStep(null)} closeOnOverlay>
          <div className="relative w-[440px] max-w-full bg-[var(--cream)] sketch-border p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-hand text-lg text-amber-600 flex items-center gap-1.5">
                  <CircleDashed size={14} /> paso pendiente
                </div>
                <div className="font-display text-xl mt-0.5" data-testid="flow-guide-title">{guideStep.label}</div>
              </div>
              <button onClick={() => setGuideStep(null)} className="p-1 rounded hover:bg-[var(--cream-soft)]" data-testid="flow-guide-close">
                <X size={16} />
              </button>
            </div>
            <p className="text-sm text-[var(--ink)] font-semibold mt-3" data-testid="flow-guide-falta">{guideStep.guide.falta}</p>
            <div className="text-xs uppercase tracking-wider text-[var(--lala-blue)] font-bold mt-3 mb-1.5">Cómo completarlo</div>
            <ol className="space-y-1.5" data-testid="flow-guide-pasos">
              {guideStep.guide.pasos.map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[var(--ink-soft)]">
                  <span className="w-5 h-5 rounded-full bg-[var(--lala-blue)] text-[var(--cream)] text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                  <span className="leading-snug">{p}</span>
                </li>
              ))}
            </ol>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setGuideStep(null)} className="text-xs text-[var(--ink-soft)] hover:underline px-2" data-testid="flow-guide-later">
                Después
              </button>
              <button onClick={() => { goTo(guideStep); setGuideStep(null); }} data-testid="flow-guide-goto"
                className="sketch-pill bg-[var(--lala-blue)] text-white px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1">
                Ir a completar <ArrowRight size={12} />
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function LevelChangeList({ changes }) {
  const lbl = (k) => (k ? (RECOGNITION_META[k]?.label || k) : "Sin datos");
  const clr = (k) => RECOGNITION_META[k]?.color || "var(--ink-soft)";
  if (!changes?.length) return <div className="text-sm text-[var(--ink-soft)]" data-testid="reclass-none">Ningún proveedor cambia de nivel con estos pesos.</div>;
  return (
    <ul className="space-y-1.5" data-testid="reclass-list">
      {changes.map((c) => (
        <li key={c.supplier_id} className="text-sm flex items-center gap-2 flex-wrap" data-testid={`reclass-${c.supplier_id}`}>
          <span className="font-semibold">{c.name}</span>
          <span className="sketch-pill px-2 py-0.5 text-[11px] font-bold text-white" style={{ background: clr(c.from) }}>{lbl(c.from)}</span>
          <span className="text-[var(--ink-soft)]">→</span>
          <span className="sketch-pill px-2 py-0.5 text-[11px] font-bold text-white" style={{ background: clr(c.to) }}>{lbl(c.to)}</span>
          <span className="text-xs text-[var(--ink-soft)]">({toBase10(c.avg_from)?.toFixed(1)} → {toBase10(c.avg_to)?.toFixed(1)} / 10)</span>
        </li>
      ))}
    </ul>
  );
}

export function WeightsTab({ allowedCategories = null, buyerMode = false }) {
  const [weights, setWeights] = useState(null);
  const [globalW, setGlobalW] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState(buyerMode && allowedCategories?.length ? allowedCategories[0] : "__global__");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [sim, setSim] = useState(null);
  const [reclass, setReclass] = useState(null);
  const [history, setHistory] = useState([]);
  const [pilotCats, setPilotCats] = useState([]);
  const [recalcPolicy, setRecalcPolicy] = useState("recalculate_history");
  const readOnly = buyerMode;

  useEffect(() => {
    Promise.all([http.get("/admin/category-weights"), http.get("/pilot-categories")]).then(([w, c]) => {
      setGlobalW(w.data.global);
      setOverrides(w.data.overrides || {});
      const initialCat = buyerMode && allowedCategories?.length ? allowedCategories[0] : "__global__";
      setWeights(initialCat === "__global__" ? w.data.global : ((w.data.overrides || {})[initialCat] || w.data.global));
      setCats(allowedCategories || c.data.all || []);
      setPilotCats(c.data.categories || []);
    });
  }, []);

  useEffect(() => {
    const params = cat !== "__global__" ? `?category=${encodeURIComponent(cat)}` : "";
    http.get(`/category-weights/history${params}`).then((r) => setHistory(r.data.items || [])).catch(() => {});
  }, [cat, msg]);

  const selectCat = (value) => {
    setCat(value);
    setMsg(""); setErr(""); setSim(null); setReclass(null);
    setWeights(value === "__global__" ? globalW : (overrides[value] || globalW));
  };
  const hasOverride = cat !== "__global__" && !!overrides[cat];

  if (!weights) return <div className="px-6 py-12 text-center font-hand text-xl text-[var(--lala-blue)]">Cargando…</div>;
  const total = Object.values(weights).reduce((a, b) => a + Number(b), 0);
  const sumOk = Math.abs(total - 100) < 0.01;
  const setW = (key, v) => setWeights({ ...weights, [key]: Number(v) });

  const distributeEvenly = () => {
    const v = +(100 / PILLARS.length).toFixed(2);
    const arr = PILLARS.map((p) => [p.key, v]);
    const remainder = +(100 - v * PILLARS.length).toFixed(2);
    arr[arr.length - 1][1] = +(v + remainder).toFixed(2);
    setWeights(Object.fromEntries(arr));
  };
  const normalize = () => {
    const t = Object.values(weights).reduce((a, b) => a + Number(b), 0);
    if (t === 0) return;
    setWeights(Object.fromEntries(Object.entries(weights).map(([k, v]) => [k, +((v * 100) / t).toFixed(2)])));
  };

  const save = async () => {
    const confirmText = cat === "__global__"
      ? "Vas a modificar los PESOS GLOBALES del programa. Este cambio es excepcional y quedará registrado en la auditoría. ¿Confirmas?"
      : `Vas a modificar los pesos de "${cat}" (${recalcPolicy === "future_only" ? "solo evaluaciones futuras" : "recalculando todo el histórico"}). Este cambio es excepcional y quedará registrado en la auditoría. ¿Confirmas?`;
    if (!window.confirm(confirmText)) return;
    setBusy(true); setErr(""); setMsg(""); setSim(null); setReclass(null);
    try {
      if (cat === "__global__") {
        await http.put("/admin/pillar-weights", { weights });
        setGlobalW(weights);
        setMsg("Pesos globales guardados.");
      } else {
        const { data } = await http.put(`/admin/category-weights/${encodeURIComponent(cat)}`, { weights, recalc_policy: recalcPolicy });
        setOverrides({ ...overrides, [cat]: weights });
        setReclass(data.reclassified || []);
        setMsg(`Pesos de "${cat}" guardados. ${data.reclassified?.length ? `${data.reclassified.length} proveedor(es) cambiaron de nivel:` : "Ningún proveedor cambió de nivel."}`);
      }
    }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const simulate = async () => {
    setBusy(true); setErr(""); setMsg(""); setSim(null); setReclass(null);
    try {
      const { data } = await http.post(`/admin/category-weights/${encodeURIComponent(cat)}/simulate`, { weights });
      setSim(data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const removeOverride = async () => {
    if (!window.confirm(`¿Quitar los pesos propios de "${cat}"? Sus proveedores volverán a usar los pesos globales.`)) {
      setMsg("Se canceló la eliminación de los pesos propios.");
      return;
    }
    setBusy(true); setErr(""); setMsg("");
    try {
      await http.delete(`/admin/category-weights/${encodeURIComponent(cat)}`);
      const next = { ...overrides };
      delete next[cat];
      setOverrides(next);
      setWeights(globalW);
      setMsg(`"${cat}" ahora usa los pesos globales.`);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const exportHistory = async () => {
    try {
      const r = await http.get("/category-weights/history/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Lala_Elite_Historial_Pesos.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErr("No se pudo descargar el historial."); }
  };

  const recompute = async () => {
    if (!window.confirm("Recalcular el score y nivel de TODAS las evaluaciones existentes con los pesos actuales?")) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      const { data } = await http.post("/admin/recompute");
      setMsg(`Recálculo completo: ${data.updated_evaluations} evaluaciones en ${data.affected_suppliers} proveedores.`);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <>
      {!buyerMode && pilotCats.length > 0 && (
        (() => {
          const pendingCats = pilotCats.filter((c) => !overrides[c]);
          return pendingCats.length > 0 ? (
            <div className="mb-4 p-4 rounded-xl bg-[#FFF8E6] border border-[#D4A437]" data-testid="pending-weights-banner">
              <div className="font-bold text-sm text-[#8a6a14]">◌ Categorías del piloto sin pesos propios</div>
              <p className="text-sm text-[#8a6a14] mt-1">
                {pendingCats.map((c, i) => (
                  <span key={c}>
                    <button onClick={() => selectCat(c)} className="underline font-semibold hover:text-[var(--lala-blue)]" data-testid={`pending-cat-${c}`}>{c}</button>
                    {i < pendingCats.length - 1 && <span>, </span>}
                  </span>
                ))}
                {" "}— hoy usan los pesos globales. Defínelos aquí con el criterio acordado con su category manager.
              </p>
            </div>
          ) : (
            <div className="mb-4 p-3 rounded-xl bg-green-50 border border-green-200 text-sm text-green-800" data-testid="pending-weights-ok">
              ✓ Todas las categorías del piloto tienen pesos propios definidos.
            </div>
          );
        })()
      )}
      <div className="sketch-border bg-[var(--cream-soft)] p-6" data-testid="weights-form">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5 pb-4 border-b border-[var(--line)]">
          <div>
            <div className="font-hand text-lg text-[var(--lala-blue)]">pesos por categoría de compra</div>
            <p className="text-xs text-[var(--ink-soft)] mt-0.5">
              Los category managers definen el criterio de negocio; <b>solo el Administrador modifica los pesos en la plataforma</b>.
              Los cambios de pesos son excepcionales y quedan en la auditoría.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select value={cat} onChange={(e) => selectCat(e.target.value)} className="input-field text-sm font-semibold" data-testid="weights-category-select">
              {!buyerMode && <option value="__global__">Global (default)</option>}
              {cats.map((c) => <option key={c} value={c}>{c}{overrides[c] ? " · pesos propios" : ""}</option>)}
            </select>
            {hasOverride && !readOnly && (
              <button onClick={removeOverride} disabled={busy} className="btn-ghost text-xs text-red-700" data-testid="weights-remove-override-btn">
                Quitar pesos propios
              </button>
            )}
            {cat !== "__global__" && !hasOverride && (
              <span className="text-xs text-[var(--ink-soft)]" data-testid="weights-using-global">usa pesos globales — edita y guarda para crear los propios</span>
            )}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          {PILLARS.map((p) => (
            <div key={p.key} data-testid={`weight-${p.key}`}>
              <div className="flex items-center justify-between">
                <label className="font-semibold">{p.label}</label>
                <div className="flex items-center gap-2">
                  <input type="number" min="0" max="100" step="0.1" value={weights[p.key]}
                    onChange={(e) => setW(p.key, e.target.value)}
                    onBlur={(e) => setW(p.key, Number(e.target.value) || 0)}
                    disabled={readOnly}
                    className="w-20 input-field text-right py-1 disabled:opacity-60" data-testid={`weight-input-${p.key}`} />
                  <span className="font-display text-2xl text-[var(--lala-blue)]">%</span>
                </div>
              </div>
              <input type="range" min="0" max="100" step="0.5" value={weights[p.key]}
                onChange={(e) => setW(p.key, e.target.value)}
                disabled={readOnly}
                className="w-full mt-2 accent-[var(--lala-blue)] disabled:opacity-50" />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-6 pt-5 border-t border-[var(--line)]">
          <div data-testid="weights-total">
            <div className="text-xs uppercase tracking-wider text-[var(--ink-soft)]">Suma total</div>
            <div className={`font-display text-4xl ${sumOk ? "text-[var(--lala-blue)]" : "text-red-600"}`}>{total.toFixed(2)}%</div>
            {!sumOk && <div className="text-xs text-red-600 mt-1">Debe ser exactamente 100%</div>}
          </div>
          <div className="flex flex-wrap gap-2">
            {!readOnly && (
              <>
            <button onClick={distributeEvenly} className="btn-ghost text-sm" data-testid="weights-even-btn">
              <Sparkles size={14} className="inline mr-1" /> Distribuir igual
            </button>
            <button onClick={normalize} className="btn-ghost text-sm" data-testid="weights-normalize-btn" disabled={total === 0}>Normalizar a 100</button>
            {cat !== "__global__" && (
              <button onClick={simulate} disabled={busy || !sumOk} className="btn-ghost text-sm border border-[var(--lala-blue)] text-[var(--lala-blue)]" data-testid="weights-simulate-btn">
                Simular impacto
              </button>
            )}
            <button onClick={save} disabled={busy || !sumOk} className="btn-primary text-sm" data-testid="weights-save-btn"><Save size={14} /> Guardar</button>
              </>
            )}
          </div>
        </div>
        {!readOnly && cat !== "__global__" && (
          <div className="mt-4 p-3 rounded-xl bg-white border border-[var(--line)]" data-testid="recalc-policy-box">
            <div className="text-xs uppercase tracking-wider font-bold text-[var(--ink-soft)] mb-2">Al guardar, ¿cómo aplicar el cambio? <span className="font-normal normal-case">(decisión pendiente de negocio — elige explícitamente)</span></div>
            <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="recalc-history-radio">
              <input type="radio" name="recalc" className="mt-0.5 accent-[var(--lala-blue)]"
                checked={recalcPolicy === "recalculate_history"}
                onChange={() => setRecalcPolicy("recalculate_history")} />
              <span><b>Recalcular todo el histórico</b> — los niveles y promedios de todos los periodos se recalculan con los nuevos pesos.</span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer mt-1.5" data-testid="recalc-future-radio">
              <input type="radio" name="recalc" className="mt-0.5 accent-[var(--lala-blue)]"
                checked={recalcPolicy === "future_only"}
                onChange={() => setRecalcPolicy("future_only")} />
              <span><b>Solo evaluaciones futuras</b> — el cambio aplica a partir del periodo actual; queda registrado desde cuándo rige.</span>
            </label>
            <p className="text-[11px] text-[var(--ink-soft)] mt-2">Usa "Simular impacto" para ver el antes/después de niveles sin guardar nada.</p>
          </div>
        )}
        {msg && <div data-testid="weights-msg" className="mt-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">{msg}</div>}
        {err && <div data-testid="weights-err" className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}
        {reclass && reclass.length > 0 && (
          <div className="mt-3 p-4 rounded-xl bg-[#FFF8E6] border border-[#D4A437]" data-testid="reclass-notice">
            <div className="font-bold text-sm text-[#8a6a14] mb-2">⚠ Proveedores reclasificados con los nuevos pesos</div>
            <LevelChangeList changes={reclass} />
          </div>
        )}
        {sim && (
          <div className="mt-3 p-4 rounded-xl bg-[var(--cream)] border border-[var(--lala-blue)]/40" data-testid="sim-panel">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="font-bold text-sm text-[var(--lala-blue)]">Simulación · {sim.category} ({sim.total} proveedores, periodo {sim.period}) — sin guardar</div>
              <div className="text-xs text-[var(--ink-soft)]" data-testid="sim-changed-count">{sim.changed.length} cambio(s) de nivel</div>
            </div>
            <LevelChangeList changes={sim.changed} />
            <p className="text-[11px] text-[var(--ink-soft)] mt-2">Presiona "Guardar" para aplicar estos pesos; nada se ha modificado aún.</p>
          </div>
        )}
        {history.length > 0 ? (
          <div className="mt-5 pt-4 border-t border-[var(--line)]" data-testid="weights-history">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="font-hand text-lg text-[var(--lala-blue)]">historial de cambios{cat !== "__global__" ? ` · ${cat}` : ""}</div>
              <button onClick={exportHistory} className="btn-ghost text-xs border border-[var(--line)]" data-testid="export-history-btn">
                ⇩ Descargar Excel (auditoría)
              </button>
            </div>
            <div className="space-y-1">
              {history.slice(0, 8).map((h, i) => (
                <div key={i} className="text-xs text-[var(--ink-soft)] flex items-center gap-2 flex-wrap" data-testid={`weights-history-row-${i}`}>
                  <span className="font-semibold text-[var(--ink)]">{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                  <span>{h.user}</span>
                  <span className="sketch-pill px-2 py-0.5 bg-[var(--cream-deep)] font-semibold">{h.category}</span>
                  <span>{h.action === "delete_category_weights" ? "quitó los pesos propios" : "actualizó pesos"}</span>
                  {h.reclassified > 0 && <span className="text-[#8a6a14] font-semibold">· {h.reclassified} reclasificado(s)</span>}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-5 pt-4 border-t border-[var(--line)]" data-testid="weights-history">
            <div className="font-hand text-lg text-[var(--lala-blue)]">historial de cambios{cat !== "__global__" ? ` · ${cat}` : ""}</div>
            <p className="text-xs text-[var(--ink-soft)] mt-1" data-testid="weights-history-empty">
              Sin cambios de pesos registrados{cat !== "__global__" ? " para esta categoría" : ""}. Cada modificación quedará aquí con fecha, usuario y política aplicada.
            </p>
          </div>
        )}
      </div>

      {!buyerMode && (
      <div className="sketch-border-soft bg-[var(--cream-soft)] p-6 mt-6">
        <div className="font-hand text-xl text-[var(--lala-blue)]">recálculo histórico</div>
        <h3 className="font-display text-2xl mb-2">Aplicar a evaluaciones existentes</h3>
        <p className="text-sm text-[var(--ink-soft)] mb-4">
          Al cambiar los pesos, las nuevas evaluaciones los usan automáticamente. Para aplicarlos a las históricas, ejecuta el recálculo. (Las calificaciones por pilar 0–10 no se modifican.)
        </p>
        <button onClick={recompute} disabled={busy} className="sketch-pill bg-[var(--cream)] px-4 py-2 text-sm font-semibold text-[var(--lala-blue)]" data-testid="recompute-btn">
          <RotateCw size={14} className="inline mr-1" /> Recalcular todas
        </button>
      </div>
      )}
    </>
  );
}

function SubKpiRow({ row, onChange, onDelete }) {
  return (
    <div className="grid grid-cols-12 gap-2 items-center py-2 border-b border-[var(--line)]" data-testid={`subkpi-row-${row.id}`}>
      <input value={row.label} onChange={(e) => onChange({ ...row, label: e.target.value })}
        className="col-span-6 input-field py-1.5 text-sm" placeholder="Etiqueta" data-testid={`subkpi-label-${row.id}`} />
      <input value={row.key} disabled className="col-span-3 input-field py-1.5 text-sm bg-[var(--cream-deep)] text-[var(--ink-soft)]" />
      <div className="col-span-2 flex items-center gap-1">
        <input type="number" min="0" step="0.5" value={row.weight}
          onChange={(e) => onChange({ ...row, weight: Number(e.target.value) })}
          className="input-field py-1.5 text-sm text-right" data-testid={`subkpi-weight-${row.id}`} />
        <span className="text-xs text-[var(--ink-soft)]">%</span>
      </div>
      <button onClick={onDelete} className="col-span-1 text-red-600 hover:bg-red-50 rounded p-1 mx-auto" title="Eliminar" data-testid={`subkpi-delete-${row.id}`}>
        <Trash2 size={16} />
      </button>
    </div>
  );
}

function SubKpisTab() {
  const [grouped, setGrouped] = useState(null);
  const [useSub, setUseSub] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const reload = async () => {
    const { data } = await http.get("/sub-kpis/config");
    setUseSub(data.use_sub_kpis);
    setGrouped(data.grouped);
  };

  useEffect(() => { reload(); }, []);

  if (!grouped) return <div className="px-6 py-12 text-center font-hand text-xl text-[var(--lala-blue)]">Cargando…</div>;

  const toggleUse = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const { data } = await http.put("/admin/sub-kpis/use", { use_sub_kpis: !useSub });
      setUseSub(data.use_sub_kpis);
      setMsg(`Captura por sub-KPIs ${data.use_sub_kpis ? "ACTIVADA" : "desactivada"}.`);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const addRow = async (pillar) => {
    const label = prompt(`Nueva sub-KPI para ${pillar}: etiqueta`);
    if (!label) return;
    const key = prompt(`Identificador (solo letras/_):`, label.toLowerCase().replace(/[^a-z0-9_]+/g, "_").slice(0, 30));
    if (!key) return;
    try {
      await http.post("/admin/sub-kpis", { pillar, key, label, weight: 0, order: (grouped[pillar]?.length || 0) + 1, active: true });
      await reload();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const saveRow = async (row) => {
    try {
      await http.put(`/admin/sub-kpis/${row.id}`, { label: row.label, weight: row.weight });
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const updateRow = (pillar, row) => {
    setGrouped({ ...grouped, [pillar]: grouped[pillar].map((r) => (r.id === row.id ? row : r)) });
  };

  const deleteRow = async (pillar, id) => {
    if (!window.confirm("¿Eliminar este sub-KPI?")) return;
    await http.delete(`/admin/sub-kpis/${id}`);
    await reload();
  };

  return (
    <div>
      <div className="sketch-border bg-[var(--cream-soft)] p-5 mb-5 flex items-center justify-between flex-wrap gap-3" data-testid="subkpi-toggle">
        <div>
          <div className="font-hand text-lg text-[var(--lala-blue)]">modo de captura</div>
          <div className="font-display text-xl">
            {useSub ? "Captura por sub-KPIs" : "Captura directa por pilar"}
          </div>
          <p className="text-xs text-[var(--ink-soft)] mt-1 max-w-md">
            Cuando está activo, el formulario de evaluación pide una calificación por cada sub-KPI y el score del pilar se calcula automáticamente.
          </p>
        </div>
        <button onClick={toggleUse} disabled={busy} className="btn-primary" data-testid="subkpi-toggle-btn">
          {useSub ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
          {useSub ? "Desactivar" : "Activar"}
        </button>
      </div>

      {msg && <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">{msg}</div>}
      {err && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}

      {PILLARS.map((p) => {
        const list = grouped[p.key] || [];
        const total = list.reduce((s, r) => s + Number(r.weight || 0), 0);
        const ok = Math.abs(total - 100) < 0.01;
        return (
          <div key={p.key} className="sketch-border bg-[var(--cream-soft)] p-5 mb-4" data-testid={`subkpi-block-${p.key}`}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-hand text-lg text-[var(--lala-blue)]">{list.length} sub-KPIs</div>
                <h3 className="font-display text-2xl">{p.label}</h3>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wider text-[var(--ink-soft)]">Suma de pesos</div>
                <div className={`font-display text-2xl ${ok ? "text-[var(--lala-blue)]" : "text-amber-600"}`}>{total.toFixed(1)}%</div>
                {!ok && <div className="text-[10px] text-amber-700">(idealmente 100)</div>}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-[var(--ink-soft)] font-bold pb-1 border-b border-[var(--line)]">
              <div className="col-span-6">Etiqueta</div>
              <div className="col-span-3">Identificador</div>
              <div className="col-span-2 text-right">Peso</div>
              <div className="col-span-1"></div>
            </div>
            {list.map((row) => (
              <SubKpiRow
                key={row.id}
                row={row}
                onChange={(r) => updateRow(p.key, r)}
                onDelete={() => deleteRow(p.key, row.id)}
              />
            ))}
            <div className="flex justify-between items-center mt-3">
              <button onClick={() => addRow(p.key)} className="btn-ghost text-xs" data-testid={`subkpi-add-${p.key}`}>
                <Plus size={14} className="inline mr-1" /> Añadir sub-KPI
              </button>
              <button
                onClick={async () => { for (const r of list) await saveRow(r); setMsg(`Guardado ${p.label}`); }}
                className="sketch-pill bg-[var(--cream)] px-3 py-1 text-xs font-semibold text-[var(--lala-blue)]"
                data-testid={`subkpi-save-${p.key}`}
              >
                <Save size={12} className="inline mr-1" /> Guardar cambios
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CertificatesCard() {
  const [available, setAvailable] = useState([]);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const TIERS = [
    { key: "partner", label: "Partner · Platino" },
    { key: "key", label: "Key · Oro" },
    { key: "challenge", label: "Challenger · Plata" },
    { key: "commercial", label: "Commercial · Bronce" },
  ];
  const load = () => http.get("/certificates/available").then((r) => setAvailable(r.data.tiers || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const upload = async (tier, file) => {
    if (!file) return;
    setBusy(tier); setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post(`/admin/certificates/${tier}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvailable(data.available || []);
      setMsg(`Arte de ${tier} actualizado. Los proveedores de ese nivel ya pueden descargar su certificado.`);
    } catch (e) {
      setMsg("Error: " + formatApiErrorDetail(e));
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="rounded-2xl border border-[var(--line)] bg-white p-5 mb-6" data-testid="certificates-card">
      <div className="flex items-center gap-2 mb-1">
        <FileText size={16} className="text-[var(--lala-blue)]" />
        <h3 className="font-display text-lg">Certificados por nivel</h3>
      </div>
      <p className="text-xs text-[var(--ink-soft)] mb-4">
        Sube el arte oficial (PNG o JPG) de cada nivel. El sistema escribe automáticamente el nombre del proveedor, su score y el período sobre el diseño. Mantén la misma plantilla base (zona derecha con nombre, SCORE: y PERIODO:) para que los textos caigan en su lugar.
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {TIERS.map((t) => (
          <div key={t.key} className="rounded-xl border border-[var(--line)] p-3" data-testid={`cert-slot-${t.key}`}>
            <div className="text-sm font-semibold">{t.label}</div>
            <div className={`text-[11px] font-bold uppercase tracking-wider mt-1 ${available.includes(t.key) ? "text-emerald-700" : "text-[var(--ink-soft)]"}`}>
              {available.includes(t.key) ? "✓ Arte cargado" : "◌ Pendiente"}
            </div>
            <label className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--lala-blue)] cursor-pointer hover:underline">
              <Upload size={12} /> {busy === t.key ? "Subiendo…" : available.includes(t.key) ? "Reemplazar arte" : "Subir arte"}
              <input type="file" accept=".png,.jpg,.jpeg" className="hidden" disabled={busy === t.key}
                data-testid={`cert-upload-${t.key}`}
                onChange={(e) => upload(t.key, e.target.files?.[0])} />
            </label>
          </div>
        ))}
      </div>
      {msg && <p className="text-xs mt-3 text-[var(--lala-blue)]" data-testid="cert-msg">{msg}</p>}
    </div>
  );
}

function ReportPolicyCard() {
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    http.get("/program/report-request-policy").then((r) => setPolicy(r.data)).catch(() => {});
  }, []);
  if (!policy) return null;
  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await http.put("/admin/program/report-request-policy", {
        mode: policy.mode,
        price_mxn: policy.mode === "paid" ? Number(policy.price_mxn) || null : null,
        message_to_supplier: policy.message_to_supplier || null,
      });
      setMsg("Guardado. Los proveedores verán el cambio al instante.");
    } catch (e) {
      setMsg("Error: " + formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };
  const MODES = [
    { v: "free", label: "Descarga directa (un clic)", desc: "El proveedor descarga su reporte PDF al instante desde 'Mi desempeño'." },
    { v: "approval", label: "Requiere autorización de Lala", desc: "El proveedor solicita el reporte y un admin lo aprueba antes de la descarga." },
    { v: "paid", label: "De pago", desc: "Igual que autorización, mostrando un costo definido por Lala." },
    { v: "disabled", label: "Deshabilitado", desc: "El proveedor no puede descargar ni solicitar el reporte." },
  ];
  return (
    <div className="rounded-2xl border border-[var(--line)] bg-white p-5 mb-6" data-testid="report-policy-card">
      <div className="flex items-center gap-2 mb-1">
        <FileText size={16} className="text-[var(--lala-blue)]" />
        <h3 className="font-display text-lg">Reporte mensual del proveedor</h3>
      </div>
      <p className="text-xs text-[var(--ink-soft)] mb-4">Define cómo obtiene el proveedor su reporte ejecutivo PDF desde su portal.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
        {MODES.map((m) => (
          <button key={m.v} onClick={() => setPolicy({ ...policy, mode: m.v })}
            data-testid={`report-policy-mode-${m.v}`}
            className={`text-left rounded-xl border p-3 transition-colors ${policy.mode === m.v ? "border-[var(--lala-blue)] bg-blue-50" : "border-[var(--line)] hover:border-[var(--lala-blue)]"}`}>
            <div className="text-sm font-semibold flex items-center gap-1.5">
              {policy.mode === m.v ? <CheckCircle2 size={14} className="text-[var(--lala-blue)]" /> : <CircleDashed size={14} className="text-[var(--ink-soft)]" />}
              {m.label}
            </div>
            <div className="text-[11px] text-[var(--ink-soft)] mt-1 leading-snug">{m.desc}</div>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        {policy.mode === "paid" && (
          <label className="text-xs text-[var(--ink-soft)]">Precio (MXN)
            <input type="number" value={policy.price_mxn ?? ""} data-testid="report-policy-price"
              onChange={(e) => setPolicy({ ...policy, price_mxn: e.target.value })}
              className="block mt-1 rounded-lg border border-[var(--line)] px-2 py-1.5 text-sm w-32" />
          </label>
        )}
        <label className="text-xs text-[var(--ink-soft)] flex-1 min-w-[260px]">Mensaje al proveedor
          <input value={policy.message_to_supplier || ""} data-testid="report-policy-message"
            onChange={(e) => setPolicy({ ...policy, message_to_supplier: e.target.value })}
            className="block mt-1 w-full rounded-lg border border-[var(--line)] px-2 py-1.5 text-sm" />
        </label>
        <button onClick={save} disabled={saving} data-testid="report-policy-save-btn"
          className="sketch-pill bg-[var(--lala-blue)] text-white px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50">
          <Save size={14} /> {saving ? "Guardando…" : "Guardar"}
        </button>
      </div>
      {msg && <p className="text-xs mt-2 text-[var(--lala-blue)]" data-testid="report-policy-msg">{msg}</p>}
    </div>
  );
}

function DocCriteriaTab() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  const reload = async () => {
    const { data } = await http.get("/doc-criteria");
    setRows(data);
  };
  useEffect(() => { reload(); }, []);

  const addCriterion = async () => {
    const code = window.prompt("Código (único, ej: ISO_14001):");
    if (!code) return;
    const label = window.prompt("Etiqueta:");
    if (!label) return;
    const pillar = window.prompt(`Pilar (uno de: ${PILLARS.map((p) => p.key).join(", ")}):`);
    if (!pillar) return;
    try {
      await http.post("/admin/doc-criteria", {
        code, label, pillar_key: pillar, level: "pillar",
        expected_doc_types: [], requires_expiration: true, active: true,
      });
      await reload();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const update = async (id, patch) => {
    try { await http.put(`/admin/doc-criteria/${id}`, patch); await reload(); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este criterio? Los documentos cargados quedarán huérfanos.")) return;
    await http.delete(`/admin/doc-criteria/${id}`);
    await reload();
  };

  return (
    <div className="space-y-4" data-testid="doc-criteria-tab">
      <div className="sketch-border bg-[var(--cream-soft)] p-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-hand text-lg text-[var(--lala-blue)]">criterios documentales</div>
          <h3 className="font-display text-2xl">Qué documentos pide cada pilar / sub-KPI</h3>
          <p className="text-xs text-[var(--ink-soft)] mt-1 max-w-xl">
            Cada criterio define qué tipo de documento espera Lala del proveedor.
            El evaluador valida tipo, emisor y vigencia al revisarlo.
          </p>
        </div>
        <button onClick={addCriterion} className="btn-primary text-sm" data-testid="add-criterion-btn">
          <Plus size={14} /> Nuevo criterio
        </button>
      </div>

      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>}

      {PILLARS.map((p) => {
        const list = rows.filter((r) => r.pillar_key === p.key);
        if (list.length === 0) return null;
        return (
          <div key={p.key} className="sketch-border bg-[var(--cream-soft)] p-4" data-testid={`criteria-pillar-${p.key}`}>
            <div className="font-display text-xl text-[var(--lala-blue)]">{p.label}</div>
            <ul className="divide-y divide-[var(--line)] mt-2">
              {list.map((c) => (
                <li key={c.id} className="py-2.5" data-testid={`criterion-${c.code}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold">{c.label} <span className="text-[10px] text-[var(--ink-soft)] uppercase tracking-wider">· {c.code}</span></div>
                      {c.description && <div className="text-xs text-[var(--ink-soft)] mt-0.5">{c.description}</div>}
                      <div className="text-[11px] mt-1">
                        <span className="text-[var(--lala-blue)] font-bold">acepta:</span>{" "}
                        {c.expected_doc_types?.length > 0 ? c.expected_doc_types.join(" · ") : "(libre)"}
                        {c.requires_expiration && <span className="ml-2 text-amber-700">· requiere vigencia</span>}
                        <span className={`ml-2 ${c.level === "sub_kpi" ? "text-[var(--lala-blue)]" : "text-[var(--ink-soft)]"}`}>· {c.level === "sub_kpi" ? "sub-KPI" : "pilar"}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 items-center">
                      <button onClick={() => update(c.id, { active: !c.active })} className="text-xs text-[var(--lala-blue)] hover:underline" data-testid={`criterion-toggle-${c.code}`}>
                        {c.active ? "Activo" : "Inactivo"}
                      </button>
                      <button onClick={() => {
                        const label = window.prompt("Nueva etiqueta:", c.label);
                        if (label) update(c.id, { label });
                      }} className="text-xs text-[var(--lala-blue)] hover:underline">Renombrar</button>
                      <button onClick={() => {
                        const csv = window.prompt("Tipos aceptados (separados por |):", (c.expected_doc_types || []).join(" | "));
                        if (csv !== null) update(c.id, { expected_doc_types: csv.split("|").map((s) => s.trim()).filter(Boolean) });
                      }} className="text-xs text-[var(--lala-blue)] hover:underline">Tipos</button>
                      <button onClick={() => remove(c.id)} className="text-xs text-red-700 hover:underline" data-testid={`criterion-delete-${c.code}`}>
                        <Trash2 size={11} className="inline" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function RoiTargetsCard() {
  const [cats, setCats] = useState([]);
  const [targets, setTargets] = useState({});
  const [dflt, setDflt] = useState({ min_pct: 1, max_pct: 2 });
  const [msg, setMsg] = useState("");

  useEffect(() => {
    Promise.all([http.get("/roi/targets"), http.get("/pilot-categories"), http.get("/roi/summary")])
      .then(([t, pc, s]) => {
        setTargets(t.data.targets || {});
        setDflt(t.data.default || { min_pct: 1, max_pct: 2 });
        const set = new Set(pc.data.categories || []);
        (s.data.by_category || []).forEach((c) => c.category !== "—" && set.add(c.category));
        setCats([...set].sort());
      }).catch(() => {});
  }, []);

  const val = (cat, k) => targets[cat]?.[k] ?? dflt[k === "min_pct" ? "min_pct" : "max_pct"];
  const setVal = (cat, k, v) =>
    setTargets({ ...targets, [cat]: { min_pct: val(cat, "min_pct"), max_pct: val(cat, "max_pct"), [k]: v } });

  const save = async () => {
    setMsg("");
    try {
      const clean = {};
      cats.forEach((c) => {
        clean[c] = { min_pct: Number(val(c, "min_pct")), max_pct: Number(val(c, "max_pct")) };
      });
      await http.put("/admin/roi/targets", { targets: clean });
      setMsg("Metas guardadas. El dashboard de Impacto económico ya las usa.");
    } catch (e) {
      setMsg("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="sketch-border bg-white p-5 mt-6" data-testid="roi-targets-card">
      <div className="font-display text-xl">Metas de economías por categoría (ROI)</div>
      <p className="text-xs text-[var(--ink-soft)] mt-1 mb-3">
        % de economías validadas vs gasto anual que se considera "en meta" en el dashboard de Impacto económico. Default del programa: {dflt.min_pct}–{dflt.max_pct}%.
      </p>
      <div className="grid sm:grid-cols-2 gap-2">
        {cats.map((c) => (
          <div key={c} className="flex items-center justify-between gap-2 border border-[var(--line)] rounded-lg px-3 py-2" data-testid={`roi-target-row-${c}`}>
            <span className="text-sm font-semibold">{c}</span>
            <span className="flex items-center gap-1 text-xs">
              <input type="number" min="0" step="0.1" value={val(c, "min_pct")}
                onChange={(e) => setVal(c, "min_pct", e.target.value)}
                className="input-field w-16 text-xs py-1" data-testid={`roi-target-min-${c}`} />
              –
              <input type="number" min="0" step="0.1" value={val(c, "max_pct")}
                onChange={(e) => setVal(c, "max_pct", e.target.value)}
                className="input-field w-16 text-xs py-1" data-testid={`roi-target-max-${c}`} />
              %
            </span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-3">
        <button onClick={save} className="btn-primary text-sm" data-testid="roi-targets-save">Guardar metas</button>
        {msg && <span className="text-xs font-semibold text-emerald-700" data-testid="roi-targets-msg">{msg}</span>}
      </div>
    </div>
  );
}

export default function Configuracion() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("weights");

  useEffect(() => {
    if (user && user.role !== "admin") nav("/dashboard");
  }, [user, nav]);

  const downloadDoc = async (slug, filename, format = "md") => {
    try {
      const res = await http.get(`/docs/${slug}`, {
        responseType: "blob",
        params: { format },
      });
      const mime =
        format === "pdf" ? "application/pdf" :
        format === "docx" ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" :
        format === "pptx" ? "application/vnd.openxmlformats-officedocument.presentationml.presentation" :
        "text/markdown";
      const ext = format === "pdf" ? ".pdf" : format === "docx" ? ".docx" : format === "pptx" ? ".pptx" : ".md";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename.replace(/\.[^.]+$/, "") + ext;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      window.alert("No se pudo descargar el documento: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="font-hand text-xl text-[var(--lala-blue)]">configuración del programa</div>
          <h1 className="font-display text-4xl">Lala Elite · Admin</h1>
          <p className="text-[var(--ink-soft)] mt-1 max-w-2xl">
            Define cómo se calcula el score Elite: ponderación entre pilares y, opcionalmente, sub-KPIs detallados por pilar.
          </p>
        </div>
        <div className="hidden" data-testid="docs-downloads-removed">
          <div className="text-xs font-hand text-[var(--lala-blue)] text-right">documentos para Lala</div>
          <div className="flex items-center gap-2 justify-end">
            <span className="text-sm font-semibold text-[var(--ink)]">Entendimiento</span>
            <button
              onClick={() => downloadDoc("entendimiento-lala", "Lala_Elite_Documento_de_Entendimiento", "pdf")}
              data-testid="download-entendimiento-pdf-btn"
              className="sketch-pill bg-[var(--lala-blue)] text-[var(--cream)] px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 hover:opacity-90"
            >
              <FileText size={14} /> PDF <Download size={12} />
            </button>
            <button
              onClick={() => downloadDoc("entendimiento-lala", "Lala_Elite_Documento_de_Entendimiento", "md")}
              data-testid="download-entendimiento-md-btn"
              className="sketch-pill bg-[var(--cream)] text-[var(--lala-blue)] border border-[var(--lala-blue)] px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 hover:bg-[var(--cream-soft)]"
            >
              MD <Download size={12} />
            </button>
          </div>
          <div className="flex items-center gap-2 justify-end">
            <span className="text-sm font-semibold text-[var(--ink)]">Fase 5 · Certificador</span>
            <button
              onClick={() => downloadDoc("fase-5-certificador", "Lala_Elite_Fase_5_Reemplazo_Certificador", "pdf")}
              data-testid="download-fase5-pdf-btn"
              className="sketch-pill bg-[var(--lala-blue)] text-[var(--cream)] px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 hover:opacity-90"
            >
              <FileText size={14} /> PDF <Download size={12} />
            </button>
            <button
              onClick={() => downloadDoc("fase-5-certificador", "Lala_Elite_Fase_5_Reemplazo_Certificador", "md")}
              data-testid="download-fase5-md-btn"
              className="sketch-pill bg-[var(--cream)] text-[var(--lala-blue)] border border-[var(--lala-blue)] px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 hover:bg-[var(--cream-soft)]"
            >
              MD <Download size={12} />
            </button>
          </div>
          <div className="flex items-center gap-2 justify-end pt-1 mt-1 border-t border-[var(--lala-blue)]/20">
            <span className="text-sm font-semibold text-[var(--lala-blue)]">Protocolo de Seguridad</span>
            <button
              onClick={() => downloadDoc("security-protocol", "Lala_Elite_Protocolo_Seguridad", "pdf")}
              data-testid="download-security-pdf-btn"
              className="sketch-pill bg-[var(--lala-blue)] text-[var(--cream)] px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 hover:opacity-90"
              title="12 páginas · diseñado para IT y Seguridad de Lala (LFPDPPP, BCP, subprocesadores)"
            >
              <FileText size={14} /> PDF <Download size={12} />
            </button>
          </div>
          <div className="flex items-center gap-2 justify-end">
            <span className="text-sm font-semibold text-[var(--lala-blue)]">Architecture Review</span>
            <button
              onClick={() => downloadDoc("architecture-review", "Lala_Elite_Architecture_Review", "pdf")}
              data-testid="download-arc-pdf-btn"
              className="sketch-pill bg-[var(--lala-blue)] text-[var(--cream)] px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 hover:opacity-90"
              title="12 páginas · stack, despliegue, integraciones y roadmap técnico"
            >
              <FileText size={14} /> PDF <Download size={12} />
            </button>
          </div>

          {/* ── Speech para junta Comercial + IT ── */}
          <div className="mt-3 pt-3 border-t border-[#FFC400]/60 bg-[#FFFBEA] -mx-3 px-3 py-2 rounded-lg">
            <div className="flex items-start gap-2 mb-2">
              <span className="text-xl">🎤</span>
              <div className="flex-1">
                <p className="font-bold text-[var(--ink)] text-sm">Speech junta Comercial + IT</p>
                <p className="text-[11px] text-[var(--ink-soft)] leading-snug">
                  Guion completo de 7 actos para leer en voz casi literal. Incluye guion, acciones en pantalla, talking points técnicos y cuentas demo.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={async () => {
                  try {
                    const res = await http.get("/admin/speech-comercial-it.pdf", { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "LALA_Elite_Speech_Comercial_IT.pdf";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (e) {
                    window.alert("No se pudo descargar el speech: " + (e.response?.data?.detail || e.message));
                  }
                }}
                data-testid="download-speech-comercial-it-btn"
                className="sketch-pill bg-[#FFC400] text-[var(--ink)] px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 hover:opacity-90"
                title="Descarga el speech para la junta de hoy (Comercial + IT)"
              >
                <FileText size={14} /> SPEECH PDF <Download size={12} />
              </button>
            </div>
          </div>

          {/* ── Paquete de revisión externa (Claude / ChatGPT / TI LALA) ── */}
          <div className="mt-4 pt-4 border-t-2 border-dashed border-[var(--lala-blue)]/30">
            <div className="flex items-start gap-3 mb-2">
              <span className="text-2xl">📦</span>
              <div className="flex-1">
                <p className="font-bold text-[var(--ink)]">Paquete de revisión externa</p>
                <p className="text-xs text-[var(--ink-soft)] leading-snug">
                  ZIP curado para revisión técnica por terceros. Incluye{" "}
                  <b>REVISION_CHECKLIST.md</b> (cruce minuta ↔ implementación),
                  README, PRD, server.py, integraciones, páginas React y último test report.
                  <br />
                  <span className="text-[var(--lala-blue)] font-semibold">Subir a Claude o ChatGPT como archivo adjunto.</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={async () => {
                  const res = await http.get("/admin/revision-pack.zip", { responseType: "blob" });
                  const blob = new Blob([res.data], { type: "application/zip" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
                  a.download = `lala-elite-revision-${date}.zip`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                data-testid="download-revision-pack-btn"
                className="sketch-pill bg-[var(--lala-blue-deep,#11142B)] text-[var(--cream)] px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 hover:opacity-90"
                title="ZIP ~4 MB con código + checklist + PRD para revisión externa"
              >
                <FileText size={14} /> Descargar ZIP de revisión <Download size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfigFlow tab={tab} setTab={setTab} nav={nav} />

      <ReportPolicyCard />

      <CertificatesCard />

      {tab === "weights" && <><WeightsTab /><RoiTargetsCard /></>}
      {tab === "recognition" && <RecognitionRulesTab />}
      {tab === "subkpis" && <SubKpisTab />}
            {tab === "criteria" && <DocCriteriaTab />}
    </div>
  );
}
